import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import * as services from "../services/customer";
import Swal from "sweetalert2";

const DriverFormDialog = ({
  open,
  onClose,
  onSuccess,
  customerData,
  initialData,
}) => {
  const isEdit = Boolean(initialData);

  const [name, setName] = useState(initialData?.name || "");
  const [surname, setSurname] = useState(initialData?.surname || "");
  const [idCard, setIdCard] = useState(initialData?.idCard || "");
  const [saving, setSaving] = useState(false);

  // error ต่อฟิลด์
  const [errors, setErrors] = useState({
    name: "",
    surname: "",
    idCard: "",
  });

  const resetForm = () => {
    setName("");
    setSurname("");
    setIdCard("");
    setErrors({ name: "", surname: "", idCard: "" });
    setSaving(false);
  };

  // ----- Utils: ตรวจเลขบัตรประชาชนไทย 13 หลัก -----
  const isValidThaiId = (raw) => {
    const s = (raw || "").replace(/\D/g, "");
    if (s.length !== 13) return false;
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(s.charAt(i), 10) * (13 - i);
    }
    const check = (11 - (sum % 11)) % 10;
    return check === parseInt(s.charAt(12), 10);
  };

  // กฎตรวจชื่อ/นามสกุลแบบเบสิค: ต้องไม่ว่าง, ยาว >= 2, และไม่ควรมีตัวเลข
  const isValidName = (v) => {
    const t = (v || "").trim();
    if (t.length < 2) return false;
    if (/\d/.test(t)) return false;
    return true;
  };

  // validate ทั้งฟอร์ม
  const validate = () => {
    const next = { name: "", surname: "", idCard: "" };
    let ok = true;

    if (!isValidName(name)) {
      next.name =
        "กรุณากรอกชื่อให้ถูกต้อง (อย่างน้อย 2 ตัวอักษร และไม่มีตัวเลข)";
      ok = false;
    }
    if (!isValidName(surname)) {
      next.surname =
        "กรุณากรอกนามสกุลให้ถูกต้อง (อย่างน้อย 2 ตัวอักษร และไม่มีตัวเลข)";
      ok = false;
    }

    const onlyDigits = (idCard || "").replace(/\D/g, "");
    if (!onlyDigits) {
      next.idCard = "กรุณากรอกเลขบัตรประชาชน";
      ok = false;
    } else if (onlyDigits.length !== 13) {
      next.idCard = "เลขบัตรประชาชนต้องมี 13 หลัก";
      ok = false;
    } else if (!isValidThaiId(onlyDigits)) {
      next.idCard = "เลขบัตรประชาชนไม่ถูกต้อง";
      ok = false;
    }

    setErrors(next);
    return ok;
  };

  // ปุ่มบันทึก disable ถ้ายังกรอกไม่ครบคร่าว ๆ (UX ลื่นขึ้น)
  const canRudimentarySubmit = useMemo(() => {
    return (
      name.trim() && surname.trim() && idCard.replace(/\D/g, "").length >= 13
    );
  }, [name, surname, idCard]);

  const handleSubmit = async () => {
    try {
      if (!customerData?.id) {
        await Swal.fire({
          icon: "error",
          title: "ไม่พบซัพพลายเออร์",
          text: "กรุณาลองใหม่อีกครั้ง",
          customClass: { container: "my-swal" },
        });
        return;
      }

      // ตรวจฟอร์ม
      if (!validate()) {
        const firstError =
          errors.name ||
          errors.surname ||
          errors.idCard ||
          "กรุณาตรวจสอบข้อมูลอีกครั้ง";
        await Swal.fire({
          icon: "warning",
          title: "ข้อมูลไม่ถูกต้อง",
          text: firstError,
          customClass: { container: "my-swal" },
        });
        return;
      }

      setSaving(true);

      const cleanId = idCard.replace(/\D/g, "");

      const newDriver = {
        name: name.trim(),
        surname: surname.trim(),
        idCard: cleanId,
        customerId: customerData.id,
      };

      const updatedDrivers = [...(customerData?.drivers || []), newDriver];
      const payload = { drivers: updatedDrivers };

      // อัปเดตครั้งเดียว
      const res = await services.updateCustomer(payload, customerData.id);

      // หา driver ที่เพิ่งบันทึกกลับมาจาก response (ถ้ามี)
      const updatedCustomer = res?.data?.data || res?.data;
      const savedDriver =
        updatedCustomer?.drivers?.find(
          (d) =>
            d?.name === newDriver.name &&
            d?.surname === newDriver.surname &&
            d?.idCard === newDriver.idCard
        ) || newDriver;

      await Swal.fire({
        icon: "success",
        title: isEdit ? "แก้ไขข้อมูลคนขับสำเร็จ" : "เพิ่มข้อมูลคนขับสำเร็จ",
        customClass: { container: "my-swal" },
      });

      onSuccess?.(savedDriver); // ส่งกลับให้ parent อัปเดตทันที
      resetForm();
      onClose?.();
    } catch (err) {
      console.error("Driver save failed:", err);
      await Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถบันทึกข้อมูลคนขับได้ กรุณาลองใหม่",
        customClass: { container: "my-swal" },
      });
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={() => {
        resetForm();
        onClose?.();
      }}
      fullWidth
    >
      <DialogTitle>
        {isEdit ? "แก้ไขข้อมูลคนขับ" : "เพิ่มข้อมูลคนขับ"}
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        <TextField
          label="ชื่อ"
          fullWidth
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((p) => ({ ...p, name: "" }));
          }}
          error={Boolean(errors.name)}
          helperText={
            errors.name || "กรอกชื่อ (อย่างน้อย 2 ตัวอักษร ไม่มีตัวเลข)"
          }
          sx={{ mt: 1 }}
        />

        <TextField
          label="นามสกุล"
          fullWidth
          value={surname}
          onChange={(e) => {
            setSurname(e.target.value);
            if (errors.surname) setErrors((p) => ({ ...p, surname: "" }));
          }}
          error={Boolean(errors.surname)}
          helperText={
            errors.surname || "กรอกนามสกุล (อย่างน้อย 2 ตัวอักษร ไม่มีตัวเลข)"
          }
        />

        <TextField
          label="เลขบัตรประชาชน (13 หลัก)"
          fullWidth
          value={idCard}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "");
            setIdCard(onlyDigits);
            if (errors.idCard) setErrors((p) => ({ ...p, idCard: "" }));
          }}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 13,
          }}
          error={Boolean(errors.idCard)}
          helperText={errors.idCard || "กรอกตัวเลข 13 หลัก"}
        />
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            resetForm();
            onClose?.();
          }}
          disabled={saving}
        >
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={!canRudimentarySubmit || saving} // กันกดซ้ำ/ฟอร์มยังไม่ครบ
        >
          {saving ? "กำลังบันทึก..." : isEdit ? "บันทึกการแก้ไข" : "บันทึก"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriverFormDialog;
